import {useFlowTheme} from '@features/tools/toolsHook';
import {captureException} from '@sentry/react';
import amplitudeService from '@features/analytics/amplitudeService';
import {colorSchema, designTokensSchema} from '@features/api/chat/schema';
import type {z} from 'zod';
import {fonts} from '@features/tools/toolsConstant';
import {trpc, type TrpcUtils} from '@features/utils/trpc';
import type {flowsUpdateSchema, Theme} from '@features/db/schema';
import {useViewport} from './components/useViewport';
import {useSetActiveScreenId} from '@features/activeScreenIdContext/useSetActiveScreenId';
import {useTriggerGeneration} from '@features/ai/streaming/useGeneration';
import {useReactFlow} from '@xyflow/react';
import {useCallback, useContext, useMemo} from 'react';
import {
  isComponentScreen,
  parseComponentScreenId,
} from './utils/componentScreenId';
import {
  invalidateFlowActiveStructures,
  invalidateScreenCaches,
} from './utils/invalidateScreenCaches';
import {FlowContext} from '@features/contexts/FlowContext';
import {
  getNodeDimensions,
  withComponentDimensions,
} from './utils/nodeDimensions';
import {useFlowsScreens} from './hooks/useFlowScreens';
import {
  buildScreenBounds,
  getViewportCenterInFlowCoordinates,
  putAtDirection,
  putInFree,
  putInViewportCenter,
} from '@features/flow/utils/screenPositioning';

// Hook to fetch all flows for a user with pagination
export const useFlows = (
  viewFilter: 'all' | 'shared-with-me' | 'created-by-me',
  pageSize: number = 20,
  sortBy: 'lastEdited' | 'createdAt' = 'lastEdited',
) => {
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} =
    trpc.flow.getVisibleFlows.useInfiniteQuery(
      {
        filter: viewFilter,
        sortBy,
        pageSize,
      },
      {
        getNextPageParam: (page) => page.cursor,
      },
    );

  const flows = data?.pages.flatMap((page) => page.flows) || [];

  const loadMoreChats = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  return {
    flows,
    isLoading,
    loadMoreChats,
    hasNextPage,
    isFetchingNextPage,
  };
};

// Hook to initialize a new flow
export const useInitFlow = () => {
  const trpcUtils = trpc.useUtils();
  const triggerGeneration = useTriggerGeneration();

  return trpc.flow.createFlow.useMutation({
    onSuccess: async ({flowId, screenIds}) => {
      // By triggering this immediately, we generate faster.
      // But more importantly: we're starting the revalidation in the
      // sendmessage-state, which displays the generating animation immediately!
      // Trigger generation for ALL screens in parallel
      for (const screenId of screenIds) {
        triggerGeneration(flowId, screenId);
      }
      // Invalidate the user's flows list to trigger a refetch
      await trpcUtils.flow.getVisibleFlows.invalidate();
    },
    onError: (error) => captureException(error),
  });
};

export const useAddScreenToFlow = () => {
  const trpcUtils = trpc.useUtils();

  return trpc.screens.addScreenToFlow.useMutation({
    onMutate: async ({flowId, screenId, x, y, themeId}) => {
      await trpcUtils.screens.getFlowScreens.cancel({flowId});

      const prevScreens = trpcUtils.screens.getFlowScreens.getData({flowId});
      if (prevScreens) {
        trpcUtils.screens.getFlowScreens.setData({flowId}, [
          ...prevScreens,
          {
            id: screenId,
            x,
            y,
            themeId: themeId ?? null,
            screenSize: null,
            hasActiveStructure: null,
            type: 'screen' as const,
          },
        ]);
      }

      return {prevScreens};
    },
    onError: (_err, {flowId}, context) => {
      if (context?.prevScreens) {
        trpcUtils.screens.getFlowScreens.setData({flowId}, context.prevScreens);
      }
    },
    onSettled: async (_data, _error, {flowId}) => {
      await trpcUtils.screens.getFlowScreens.invalidate({flowId});
    },
  });
};

export const useDuplicateScreenActiveStructure = () => {
  const trpcUtils = trpc.useUtils();
  const {fitViewToChat} = useViewport();
  const setScreenId = useSetActiveScreenId();
  const {flow} = useContext(FlowContext);
  const {getNode, screenToFlowPosition} = useReactFlow();
  const {data: screens = []} = useFlowsScreens(flow.id);
  const screensWithDimensions = useMemo(
    () => withComponentDimensions(screens),
    [screens],
  );

  const getViewportCenter = useCallback(
    () =>
      getViewportCenterInFlowCoordinates({
        screenToFlowPosition,
      }),
    [screenToFlowPosition],
  );

  const onDuplicateSuccess = useCallback(
    async (flowId: string, screenId: string) => {
      amplitudeService.logEvent('[Flow] screen duplicate success');
      await invalidateScreenCaches(trpcUtils, flowId);
      setScreenId(screenId);
      requestAnimationFrame(() => {
        void fitViewToChat(screenId);
      });
    },
    [trpcUtils, setScreenId, fitViewToChat],
  );

  const dbMutation = trpc.screens.duplicateScreenActiveStructure.useMutation({
    onSuccess: ({flowId, screenId}) => onDuplicateSuccess(flowId, screenId),
    onError: (error) => captureException(error),
  });

  const componentMutation = trpc.screens.copyComponentScreen.useMutation({
    onSuccess: ({screenId}) => {
      const {flowId} = parseComponentScreenId(screenId);
      return onDuplicateSuccess(flowId, screenId);
    },
    onError: (error) => captureException(error),
  });

  const dbMutateAsync = dbMutation.mutateAsync;
  const componentMutateAsync = componentMutation.mutateAsync;
  const duplicate = useCallback(
    async (screenId: string) => {
      const existingBounds = buildScreenBounds({
        screens: screensWithDimensions,
        flowGenType: flow.genType,
        getNode,
        screenToFlowPosition,
      });
      const sourceBounds = existingBounds.find(
        (screen) => screen.id === screenId,
      );
      const sourceScreen = screensWithDimensions.find(
        (screen) => screen.id === screenId,
      );
      const size = sourceBounds
        ? {width: sourceBounds.width, height: sourceBounds.height}
        : getNodeDimensions(
            sourceScreen?.screenSize ??
              (flow.genType === 'components' ? 'desktop' : flow.genType),
          );
      const position = sourceBounds
        ? putAtDirection(existingBounds, sourceBounds, size, 'right')
        : putInFree(
            existingBounds,
            putInViewportCenter(getViewportCenter(), size),
            size,
          );

      if (isComponentScreen(screenId)) {
        const {flowId} = parseComponentScreenId(screenId);
        await componentMutateAsync({
          flowId,
          sourceScreenId: screenId,
          x: position.x,
          y: position.y,
        });
        return;
      }

      await dbMutateAsync({screenId, position});
    },
    [
      getNode,
      screenToFlowPosition,
      getViewportCenter,
      dbMutateAsync,
      componentMutateAsync,
      screensWithDimensions,
      flow.genType,
    ],
  );

  return {
    isPending: dbMutation.isPending || componentMutation.isPending,
    duplicate,
  };
};

export const useDeleteScreen = (flowId: string) => {
  const trpcUtils = trpc.useUtils();
  return trpc.screens.deleteScreen.useMutation({
    onSuccess: async (_, {screens: deletedScreens}) => {
      await Promise.all([
        trpcUtils.themes.getComponentFlowTokens.invalidate({flowId}),
        trpcUtils.screens.getFlowScreens.invalidate({flowId}),
        trpcUtils.flow.getUndoStatus.invalidate({flowId}),
        ...deletedScreens.map((screenId) =>
          Promise.all([
            trpcUtils.screens.getActiveStructure.invalidate({screenId}),
            trpcUtils.screens.getScreen.invalidate({screenId}),
          ]),
        ),
      ]);
    },
  });
};

export const useUpdateFlow = () => {
  const trpcUtils = trpc.useUtils();

  // Source: https://tanstack.com/query/v5/docs/framework/react/guides/optimistic-updates
  return trpc.flow.updateFlow.useMutation({
    onMutate: ({flowId, updates}) =>
      optimisticallyUpdateFlow(trpcUtils, flowId, updates),
    // If the mutation fails, use the context we returned above
    onError: (_err, _newFlow, context) => {
      context?.revertOptimisticFlowUpdate();
    },
    // Always refetch after error or success:
    onSettled: (_data, _error, {flowId}) =>
      Promise.all([
        trpcUtils.flow.getFlow.invalidate({flowId}),
        trpcUtils.flow.getVisibleFlows.invalidate(),
      ]),
  });
};

export async function optimisticallyUpdateFlow(
  trpcUtils: TrpcUtils,
  flowId: string,
  flowUpdates: z.infer<typeof flowsUpdateSchema>,
) {
  // Cancel any outgoing refetches
  // (so they don't overwrite our optimistic update)
  await trpcUtils.flow.getFlow.cancel({flowId});
  await trpcUtils.flow.getVisibleFlows.cancel();

  // Snapshot the previous value
  const previousFlow = trpcUtils.flow.getFlow.getData({flowId});

  if (!previousFlow) return;

  trpcUtils.flow.getFlow.setData({flowId}, {...previousFlow, ...flowUpdates});

  return {
    revertOptimisticFlowUpdate: () => {
      trpcUtils.flow.getFlow.setData({flowId}, previousFlow);
    },
  };
}

export function useChangeFlowVersion() {
  const utils = trpc.useUtils();

  return trpc.flow.setFlowVersion.useMutation({
    onSuccess: async (res, {flowId}) => {
      await Promise.all([
        utils.screens.getScreensWithStructure.invalidate(),
        utils.screens.getStructureList.invalidate(),
        utils.screens.getScreen.invalidate(),
        invalidateFlowActiveStructures(utils, flowId),
        utils.flow.getFlow.invalidate({flowId}),
        utils.screens.getFlowScreens.invalidate({flowId}),
        utils.flow.getUndoStatus.invalidate({flowId}),
        utils.themes.getComponentFlowTokens.invalidate({flowId}),
      ]);
    },
  });
}

export const useUpdateScreenOrder = () => {
  const trpcUtils = trpc.useUtils();

  return trpc.screens.updateScreenOrder.useMutation({
    onMutate: async ({flowId, screenIds}) => {
      await trpcUtils.screens.getFlowScreens.cancel({flowId});
      const previousScreens = trpcUtils.screens.getFlowScreens.getData({
        flowId,
      });
      if (previousScreens) {
        const reorderedScreens = screenIds
          .map((id) => previousScreens.find((s) => s.id === id))
          .filter((s): s is NonNullable<typeof s> => s !== undefined);
        trpcUtils.screens.getFlowScreens.setData({flowId}, reorderedScreens);
      }
      return {previousScreens};
    },
    onError: (_error, {flowId}, context) => {
      if (context?.previousScreens) {
        trpcUtils.screens.getFlowScreens.setData(
          {flowId},
          context.previousScreens,
        );
      }
    },
    onSettled: async (_data, _error, {flowId}) => {
      await trpcUtils.screens.getFlowScreens.invalidate({flowId});
    },
  });
};

export const getThemeColorProperties = (theme: Theme) => {
  return colorSchema.strip().parse(theme);
};

export const useFlowThemeProperties = (
  flowId: string,
  screenId: string | undefined,
) => {
  const flow = useContext(FlowContext).flow;
  const theme = useFlowTheme(flowId, screenId, flow);
  const componentsTheme = trpc.themes.getComponentFlowTokens.useQuery({flowId});

  if (flow.genType === 'components') {
    return componentsTheme.data ?? undefined;
  }

  if (!theme) return undefined;

  const {fontFamilyBody, ...themeProps} = designTokensSchema
    .strip()
    .parse(theme);

  return {
    ...themeProps,
    '--font-family-body': fonts[fontFamilyBody].value.style.fontFamily,
  };
};
